'use client';

import { Loader2, Upload, X } from 'lucide-react';
import * as React from 'react';

import { isSetupUploadableImage } from '@/components/setup/experience/lib/setupImageUploadUtils';
import { useUploadImage } from '@/lib/api/images';
import { cn } from '@/lib/utils';

const NON_SVG_IMAGE_MIME = 'image/jpeg,image/jpg,image/png,image/gif,image/webp';

interface SetupExperienceVariantImageUploadProps {
  id: string;
  url: string;
  onImageAvailable: (url: string) => void;
  disabled?: boolean;
  uploadingLabel?: string;
  uploadingHint?: string;
  changeLabel?: string;
  uploadLabel?: string;
  removeLabel?: string;
  invalidTypeMessage?: string;
  /** External validation state (e.g. "image required") to expose on the upload trigger. */
  invalid?: boolean;
  describedBy?: string;
}

export function SetupExperienceVariantImageUpload({
  id,
  url,
  onImageAvailable,
  disabled = false,
  uploadingLabel = 'Uploading…',
  uploadingHint = 'Please wait',
  changeLabel = 'Change',
  uploadLabel = 'Upload',
  removeLabel = 'Remove image',
  invalidTypeMessage = 'Unsupported format. Use JPG, PNG, GIF or WebP.',
  invalid = false,
  describedBy,
}: SetupExperienceVariantImageUploadProps) {
  const { mutate: uploadImage } = useUploadImage();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const uploadSeqRef = React.useRef(0);
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  const handleFile = (file: File) => {
    if (!isSetupUploadableImage(file)) {
      setUploadError(invalidTypeMessage);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    const seq = ++uploadSeqRef.current;
    setUploadError(null);
    setIsUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    uploadImage(formData, {
      onSuccess: (data) => {
        if (seq !== uploadSeqRef.current) return;
        onImageAvailable(data.file.url);
      },
      onError: (error) => {
        if (seq !== uploadSeqRef.current) return;
        setUploadError(error.message || 'Upload failed');
      },
      onSettled: () => {
        if (seq !== uploadSeqRef.current) return;
        setIsUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      },
    });
  };

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    const file = (event.target as HTMLInputElement).files?.item(0);
    if (file) handleFile(file);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    uploadSeqRef.current += 1;
    setIsUploading(false);
    setUploadError(null);
    onImageAvailable('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const openPicker = () => {
    if (!disabled && !isUploading) inputRef.current?.click();
  };

  const hasImage = Boolean(url?.trim());
  const uploadErrorId = `${id}-upload-error`;
  const triggerDescribedBy =
    [uploadError ? uploadErrorId : null, describedBy].filter(Boolean).join(' ') || undefined;

  return (
    <div className="relative w-[4.5rem]">
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={NON_SVG_IMAGE_MIME}
        disabled={isUploading || disabled}
        onInput={handleInput}
        className="sr-only"
        aria-label={hasImage ? changeLabel : uploadLabel}
      />

      <button
        type="button"
        className={cn(
          'group/slot relative flex h-[4.5rem] w-[4.5rem] cursor-pointer items-center justify-center overflow-hidden rounded-[0.625rem] border border-dashed border-[color-mix(in_oklch,var(--border)_90%,transparent)] bg-[color-mix(in_oklch,var(--muted)_35%,var(--card))] p-0 transition-[border-color,background] duration-150 hover:enabled:border-[var(--crm-selection-border)] hover:enabled:bg-[color-mix(in_oklch,var(--primary)_6%,var(--background))]',
          hasImage && 'border-solid',
          isUploading && 'cursor-wait',
        )}
        onClick={openPicker}
        disabled={disabled || isUploading}
        aria-labelledby={id}
        aria-invalid={invalid || Boolean(uploadError) || undefined}
        aria-describedby={triggerDescribedBy}
      >
        {isUploading ? (
          <span className="text-primary flex items-center justify-center" role="status">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            <span className="sr-only">{uploadingLabel}</span>
          </span>
        ) : hasImage ? (
          <>
            <img src={url} alt="" className="block h-full w-full object-cover" />
            <span className="absolute inset-x-0 bottom-0 bg-[linear-gradient(to_top,rgb(0_0_0_/_0.72),transparent)] px-1 py-[0.2rem] text-center text-[0.5625rem] font-semibold uppercase tracking-[0.02em] text-white opacity-0 transition-opacity duration-150 group-hover/slot:opacity-100">
              {changeLabel}
            </span>
          </>
        ) : (
          <span className="flex items-center justify-center text-[color-mix(in_oklch,var(--muted-foreground)_82%,transparent)]">
            <Upload className="size-4" strokeWidth={2} aria-hidden />
          </span>
        )}
      </button>

      {hasImage && !isUploading ? (
        <button
          type="button"
          className="absolute right-[-0.3125rem] top-[-0.3125rem] z-[2] flex h-[1.125rem] w-[1.125rem] cursor-pointer items-center justify-center rounded-full border border-[color-mix(in_oklch,var(--background)_70%,transparent)] bg-[color-mix(in_oklch,var(--foreground)_78%,#000)] p-0 text-white shadow-[0_1px_4px_rgb(0_0_0_/_0.28)] transition-[transform,background] duration-150 hover:scale-105 hover:bg-[color-mix(in_oklch,var(--destructive)_82%,#000)]"
          onClick={handleClear}
          disabled={disabled}
          aria-label={removeLabel}
        >
          <X className="size-3" strokeWidth={2.5} aria-hidden />
        </button>
      ) : null}

      {uploadError ? (
        <p
          id={uploadErrorId}
          className="text-destructive m-0 mb-0 mt-1 text-[0.625rem] leading-[1.25]"
          role="alert"
        >
          {uploadError}
        </p>
      ) : null}

      {isUploading ? (
        <p
          className="mb-0 mt-1 text-[0.625rem] leading-[1.25] text-[color-mix(in_oklch,var(--muted-foreground)_88%,transparent)]"
          aria-live="polite"
        >
          {uploadingHint}
        </p>
      ) : null}
    </div>
  );
}
